export const DETAILED_ANALYSIS_HEADINGS = [
  "Coach's Read",
  'What to Preserve',
  'How the Session Developed',
  'Proven Pace Gaps',
  'Technique Priorities',
  'Pattern Across the Track',
  'Next Session Plan',
];

export const DETAILED_NARRATIVE_HEADINGS = [
  "Coach's Read",
  'How the Session Developed',
];

function normalizeNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? String(parsed) : value;
}

export function numericTokens(text) {
  return [...String(text || '').matchAll(/(^|[^A-Za-z])([-+]?\d+(?:\.\d+)?)/g)]
    .map(match => normalizeNumber(match[2]));
}

export function validateDetailedAnalysis(text, meta = {}) {
  const errors = [];
  const source = String(text || '');
  let priorIndex = -1;
  for (const heading of DETAILED_ANALYSIS_HEADINGS) {
    const escaped = heading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = new RegExp(`^##\\s+${escaped}\\s*$`, 'mi').exec(source);
    if (!match) {
      errors.push(`Missing section: ${heading}`);
    } else if (match.index < priorIndex) {
      errors.push(`Section out of order: ${heading}`);
    } else {
      priorIndex = match.index;
    }
  }

  const allowed = new Set((meta.allowedNumericFacts || []).map(normalizeNumber));
  if (allowed.size) {
    const unexpected = [...new Set(numericTokens(source).filter(value => !allowed.has(value)))];
    if (unexpected.length) errors.push(`Unsupported numbers: ${unexpected.join(', ')}`);
  }

  const prioritySection = source.split(/^##\s+Technique Priorities\s*$/mi)[1]
    ?.split(/^##\s+/m)[0] || '';
  for (const corner of meta.priorityCorners || []) {
    const label = String(corner.label || corner.corner_id);
    if (!prioritySection.toLowerCase().includes(label.toLowerCase())) {
      errors.push(`Missing priority corner: ${label}`);
      continue;
    }
    const subsection = prioritySection.split(/^###\s+/m).slice(1).find(block => (
      block.split('\n', 1)[0].toLowerCase().startsWith(label.toLowerCase())
    )) || '';
    if (!/clean passes?/i.test(subsection)) {
      errors.push(`Missing validation check: ${label}`);
    }
    for (const [field, description] of [
      ['own_current_mph', 'current self-reference speed'],
      ['own_reference_mph', 'faster self-reference speed'],
    ]) {
      const required = corner[field];
      const measured = numericTokens(subsection)
        .map(Number)
        .filter(Number.isFinite);
      // Mistral often renders a supplied one-decimal speed as the nearest whole
      // mph. That preserves the coaching fact; a different measured reference
      // (for example 78.2 becoming 80) still fails this tolerance.
      if (required != null && !measured.some(value => Math.abs(value - Number(required)) <= 0.51)) {
        errors.push(`Missing ${description}: ${label}`);
      }
    }
  }

  errors.push(...detailedSemanticErrors(source));

  return errors;
}

function sectionBody(text, heading) {
  const escaped = heading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const source = String(text || '');
  const match = new RegExp(`^##\\s+${escaped}\\s*$`, 'mi').exec(source);
  if (!match) return '';
  const remainder = source.slice(match.index + match[0].length);
  const nextHeading = remainder.search(/^##\s+/m);
  return (nextHeading >= 0 ? remainder.slice(0, nextHeading) : remainder).trim();
}

export function composeDetailedAnalysis(narrative, sections = {}) {
  const coachRead = sectionBody(narrative, "Coach's Read")
    || sections.coach_read_fallback
    || 'The verified session analysis is ready.';
  const development = sectionBody(narrative, 'How the Session Developed')
    || sections.development_fallback
    || 'The available representative laps did not establish a separate progression.';
  return [
    `## Coach's Read\n${coachRead}`,
    `## What to Preserve\n${sections.what_to_preserve || 'No separate measured strength passed the reporting threshold.'}`,
    `## How the Session Developed\n${development}`,
    `## Proven Pace Gaps\n${sections.proven_pace_gaps || 'No separate proven-pace gap passed the reporting threshold.'}`,
    `## Technique Priorities\n${sections.priority_corners || 'No corner passed the clean-pass and measured-issue threshold.'}`,
    `## Pattern Across the Track\n${sections.pattern_across_track || 'The verified evidence is corner-specific rather than one track-wide habit.'}`,
    `## Next Session Plan\n${sections.next_session_plan || 'Produce clean, repeatable opening laps before attempting a corner-specific change.'}`,
  ].join('\n\n');
}

export function validateDetailedNarrative(text, meta = {}) {
  const errors = [];
  const source = String(text || '');
  let priorIndex = -1;
  for (const heading of DETAILED_NARRATIVE_HEADINGS) {
    const escaped = heading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = new RegExp(`^##\\s+${escaped}\\s*$`, 'mi').exec(source);
    if (!match) errors.push(`Missing section: ${heading}`);
    else if (match.index < priorIndex) errors.push(`Section out of order: ${heading}`);
    else priorIndex = match.index;
  }
  const unexpectedHeadings = [...source.matchAll(/^##\s+(.+?)\s*$/gm)]
    .map(match => match[1])
    .filter(heading => !DETAILED_NARRATIVE_HEADINGS.includes(heading));
  if (unexpectedHeadings.length) {
    errors.push(`Unexpected sections: ${[...new Set(unexpectedHeadings)].join(', ')}`);
  }
  const allowed = new Set((meta.allowedNumericFacts || []).map(normalizeNumber));
  if (allowed.size) {
    const unexpected = [...new Set(numericTokens(source).filter(value => !allowed.has(value)))];
    if (unexpected.length) errors.push(`Unsupported numbers: ${unexpected.join(', ')}`);
  }
  errors.push(...detailedSemanticErrors(source));
  return errors;
}

export function detailedSemanticErrors(source) {
  const errors = [];
  const semanticGuardrails = [
    [/\b(?:available[^.\n]{0,24}grip|grip[^.\n]{0,24}available)\b/i,
      'Driver score was misrepresented as available grip.'],
    [/\bdriver score\b[^.\n]{0,100}\b(?:indicates|means|shows|room|limit)\b/i,
      'Driver score was given an unsupported performance interpretation.'],
    [/\b(?:conditions?[^.\n]{0,40}(?:favorable|favourable|unfavorable|unfavourable)|(?:favorable|favourable|unfavorable|unfavourable)[^.\n]{0,40}conditions?)\b/i,
      'Conditions were given an unsupported favorable/unfavorable judgment.'],
    [/\b(?:adaptability|adapted|adapting)\b[^.\n]{0,80}\bconditions?\b/i,
      'Conditions were used as unsupported evidence of adaptability.'],
    [/\bsecondary focus\b/i, 'A secondary focus was invented.'],
    [/\b(?:CURRENT OBSERVATION|OWN FASTER PASS|DO NOT CLAIM|VALIDATE|TARGET\s+\d+)\b/,
      'Internal evidence labels leaked into the report.'],
    [/\b[A-Z]{2,}(?:_[A-Z]{2,})+\b/,
      'An internal evidence category code leaked into the report.'],
    [/\bpotential (?:for|of) (?:an? )?faster lap\b/i,
      'The lap-assembly diagnostic was misrepresented as predicted potential.'],
    [/\bestimated (?:time )?(?:gain|opportunity)\b/i,
      'A corner priority was misrepresented as a lap-time estimate.'],
    [/\bconditions?[^.\n]{0,80}\b(?:helped|hurt|hindered|did not hinder|benefited)\b/i,
      'Conditions were given an unsupported performance effect.'],
    [/\b(?:conditions?|weather|temperatures?)[^.\n]{0,100}\b(?:that\s+)?(?:didn['’]?t|did not|doesn['’]?t|does not)\s+(?:help|hurt|hinder|affect)\b/i,
      'Conditions were given an unsupported performance effect.'],
    [/\b(?:clean (?:full-lap )?sector composite|theoretical best)\b[^.\n]{0,100}\b(?:more speed|speed available|faster lap)\b/i,
      'The lap-assembly diagnostic was misrepresented as available performance.'],
    [/\b(?:session|day) (?:began|started)\b/i,
      'Unsupported early-session chronology was invented.'],
  ];
  for (const [pattern, message] of semanticGuardrails) {
    if (pattern.test(source)) errors.push(message);
  }
  return errors;
}

export function validateDetailedChatAnswer(text, meta = {}) {
  const errors = [];
  const source = String(text || '');
  if (!/^###\s+Based on your analysis\s*$/mi.test(source)) {
    errors.push('Missing grounding heading.');
  }

  const allowed = new Set((meta.allowedNumericFacts || []).map(normalizeNumber));
  if (allowed.size) {
    const unexpected = [...new Set(numericTokens(source).filter(value => !allowed.has(value)))];
    if (unexpected.length) errors.push(`Unsupported numbers: ${unexpected.join(', ')}`);
  }
  errors.push(...detailedSemanticErrors(source));
  return errors;
}
